/* eslint-disable no-nested-ternary */

import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Stack,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Typography,
  IconButton,
  CircularProgress,
  Paper, // Used for clickable cards
  ButtonBase, // Used for clickable cards
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Modal,
  Divider,
  Avatar, // For layout of main cards
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { ArrowLeft, Banknote, CreditCard, ChevronUp, ChevronDown, Settings } from 'lucide-react'; // Using Lucide icons
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, TouchSensor } from '@dnd-kit/core';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { CSS } from '@dnd-kit/utilities';
import { getManualFundingAccount, updateManualFundingAccount } from '../api/payment.api';
import { getAppSetting, updateAppSetting } from '../api/services';
import { SortableProcessorCard } from './components/SortableProcessorCard';

// Reusable tag/chip input component for entering multiple BVNs or NINs easily
function MultipleInput({ label, placeholder, values = [], usage = {}, onChange }) {
  const [inputValue, setInputValue] = useState('');

  const addValues = (valString) => {
    // split by comma, space, semicolon or newline
    const items = valString
      .split(/[\s,;\n]+/)
      .map((item) => item.trim())
      .filter((item) => item !== '');

    const validItems = [];
    const invalidItems = [];

    items.forEach((item) => {
      // Must be exactly 11 numeric digits
      if (/^\d{11}$/.test(item)) {
        if (!values.includes(item)) {
          validItems.push(item);
        }
      } else {
        invalidItems.push(item);
      }
    });

    if (invalidItems.length > 0) {
      toast.error(`Invalid entry: BVN or NIN must be exactly 11 numeric digits. Ignored: ${invalidItems.join(', ')}`);
    }

    if (validItems.length > 0) {
      onChange([...values, ...validItems]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addValues(inputValue);
      setInputValue('');
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addValues(inputValue);
      setInputValue('');
    }
  };

  const handleDelete = (indexToDelete) => {
    onChange(values.filter((_, idx) => idx !== indexToDelete));
  };

  return (
    <Box sx={{ mt: 1 }}>
      <TextField
        label={label}
        placeholder={placeholder || 'Type and press Enter or paste list'}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        fullWidth
        size="small"
        helperText={`Total added: ${
          values?.length || 0
        }. Paste multiple values separated by spaces, commas, or newlines.`}
        FormHelperTextProps={{ sx: { fontSize: 10 } }}
      />
      {values && values.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            mt: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'grey.300',
            bgcolor: 'grey.50',
            maxHeight: 120,
            overflowY: 'auto',
          }}
        >
          {values.map((val, idx) => (
            <Chip
              key={idx}
              label={`${val} (${usage[val] || 0})`}
              onDelete={() => handleDelete(idx)}
              color="primary"
              variant="outlined"
              size="small"
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

MultipleInput.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.string),
  usage: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

// Define the categories for the main Payment Settings view
const paymentCategories = [
  {
    id: 'manual-funding-account',
    title: 'Manual Funding Account',
    description: 'Manage bank details for manual deposits that will be shown to users.',
    icon: <Banknote size={24} />,
  },
  {
    id: 'payment-processors',
    title: 'Payment Processors',
    description: 'Configure API keys and settings for payment gateways.',
    icon: <CreditCard size={24} />,
  },
];

// Payment Processor Card component (kept as a sub-component for clarity)
function PaymentProcessorCard({ processor, baseUrl, onChange, onSave, loading, onMove, isFirst, isLast }) {
  const [open, setOpen] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: processor.name_id,
  });

  const handleFieldChange = (field, value) => {
    if (field === 'banks' && Array.isArray(value)) {
      onChange({ ...processor, [field]: value?.filter((v) => v !== '').join(',') });
      return;
    }
    onChange({ ...processor, [field]: value });
    if (field === 'isActive') {
      setTimeout(() => {
        onSave();
      }, 1000);
    }
  };

  return (
    <>
      {/* CARD */}
      <Paper
        ref={setNodeRef}
        style={{
          transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
          transition,
        }}
        sx={{
          p: { xs: 2.5, sm: 3 },
          mb: 3,
          borderRadius: 4,
          border: '1px solid',
          borderColor: processor.isActive ? 'primary.main' : 'grey.200',
          borderLeft: '5px solid',
          borderLeftColor: processor.isActive ? 'primary.main' : 'grey.400',
          boxShadow: processor.isActive ? '0 10px 30px rgba(28, 78, 93, 0.08)' : '0 4px 12px rgba(0,0,0,0.03)',
          bgcolor: '#ffffff',
          position: 'relative',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            borderColor: 'primary.main',
            boxShadow: '0 12px 35px rgba(28, 78, 93, 0.12)',
          },
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          {/* TOP / LEFT: Header block containing avatar, name, chip, and switch (only on mobile) */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            width={{ xs: '100%', sm: 'auto' }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  fontWeight: 800,
                  fontSize: '1.25rem',
                  bgcolor: processor.isActive ? 'primary.main' : 'grey.100',
                  color: processor.isActive ? 'common.white' : 'text.secondary',
                  border: '1.5px solid',
                  borderColor: processor.isActive ? 'primary.light' : 'grey.300',
                  boxShadow: processor.isActive ? '0 2px 8px rgba(28, 78, 93, 0.2)' : 'none',
                }}
              >
                {processor.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography fontWeight={800} fontSize={17} color="primary.dark">
                  {processor.name}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1.5,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: processor.isActive ? 'success.lighter' : 'grey.100',
                      color: processor.isActive ? 'success.darker' : 'text.secondary',
                      border: '1px solid',
                      borderColor: processor.isActive ? 'success.light' : 'grey.200',
                    }}
                  >
                    {processor.isActive ? 'Active' : 'Inactive'}
                  </Box>
                  {processor.isHidden && (
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 1,
                        py: 0.25,
                        borderRadius: 1.5,
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: 'warning.light',
                        color: 'warning.dark',
                        border: '1px solid',
                        borderColor: 'warning.main',
                      }}
                    >
                      Hidden
                    </Box>
                  )}
                </Stack>
              </Box>
            </Stack>

            {/* Switch on top-right only on mobile screen widths */}
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <Switch checked={processor.isActive} onChange={(e) => handleFieldChange('isActive', e.target.checked)} />
            </Box>
          </Stack>

          {/* BOTTOM / RIGHT: Action block */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              mt: { xs: 2, sm: 0 },
              borderTop: { xs: '1px solid', sm: 'none' },
              borderColor: 'grey.100',
              pt: { xs: 2, sm: 0 },
            }}
          >
            {/* Desktop Switch (hidden on mobile header) */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Switch checked={processor.isActive} onChange={(e) => handleFieldChange('isActive', e.target.checked)} />
            </Box>

            <Button
              variant="outlined"
              onClick={() => setOpen(true)}
              startIcon={<Settings size={16} />}
              sx={{
                borderRadius: 2.5,
                px: { xs: 2.5, sm: 2 },
                height: 42,
                fontWeight: 700,
                borderColor: 'primary.light',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.lighter',
                },
              }}
            >
              Configure
            </Button>

            {/* Larger Unified Up/Down Control Pill */}
            <Stack
              direction="row"
              sx={{
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2.5,
                p: 0.5,
                height: 42,
                alignItems: 'center',
              }}
            >
              <IconButton
                size="medium"
                disabled={isFirst || loading}
                onClick={() => onMove(processor.name_id, 'up')}
                sx={{
                  p: 0.8,
                  color: 'primary.main',
                  '&.Mui-disabled': { color: 'grey.300' },
                  '&:hover': { bgcolor: 'grey.200' },
                }}
              >
                <ChevronUp size={20} />
              </IconButton>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <IconButton
                size="medium"
                disabled={isLast || loading}
                onClick={() => onMove(processor.name_id, 'down')}
                sx={{
                  p: 0.8,
                  color: 'primary.main',
                  '&.Mui-disabled': { color: 'grey.300' },
                  '&:hover': { bgcolor: 'grey.200' },
                }}
              >
                <ChevronDown size={20} />
              </IconButton>
            </Stack>

            {/* Drag Handle Box */}
            <Box
              {...attributes}
              {...listeners}
              sx={{
                cursor: 'grab',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: 1.5,
                color: 'text.secondary',
                opacity: isDragging ? 0.5 : 0.7,
                '&:hover': {
                  bgcolor: 'grey.100',
                  color: 'primary.main',
                  opacity: 1,
                },
                transition: 'all 0.2s',
              }}
            >
              <DragIndicatorIcon fontSize="medium" />
            </Box>
          </Stack>
        </Stack>
      </Paper>

      {/* MODAL */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2,
          }}
        >
          <Paper
            sx={{
              width: '100%',
              maxWidth: 620,
              maxHeight: '90vh',
              borderRadius: 4,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* HEADER */}
            <Box
              sx={{
                p: 2.5,
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                bgcolor: 'grey.50',
              }}
            >
              <Typography fontWeight={700}>Configure {processor.name}</Typography>
              <Typography fontSize={13} color="text.secondary">
                Manage API keys, fees and behaviour
              </Typography>
            </Box>

            {/* BODY */}
            <Box sx={{ p: 2.5, overflowY: 'auto' }}>
              <Stack spacing={2}>
                {processor.publicKey !== undefined && (
                  <TextField
                    label="Public Key"
                    value={processor.publicKey}
                    onChange={(e) => handleFieldChange('publicKey', e.target.value)}
                    fullWidth
                  />
                )}

                {processor.apiKey !== undefined && (
                  <TextField
                    label="API Key"
                    value={processor.apiKey}
                    onChange={(e) => handleFieldChange('apiKey', e.target.value)}
                    fullWidth
                  />
                )}

                <TextField
                  label="Secret Key"
                  value={processor.secretKey}
                  onChange={(e) => handleFieldChange('secretKey', e.target.value)}
                  fullWidth
                />

                {processor.contactCode !== undefined && (
                  <TextField
                    label="Contact Code"
                    value={processor.contactCode}
                    onChange={(e) => handleFieldChange('contactCode', e.target.value)}
                    fullWidth
                  />
                )}

                {processor.businessId !== undefined && (
                  <TextField
                    label="Business ID"
                    value={processor.businessId}
                    onChange={(e) => handleFieldChange('businessId', e.target.value)}
                    fullWidth
                  />
                )}

                <Divider />

                {/* SWITCHES */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={processor.createOnUserCreation}
                      onChange={(e) => handleFieldChange('createOnUserCreation', e.target.checked)}
                    />
                  }
                  label="Create Account on User Creation"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={processor.isHidden || false}
                      onChange={(e) => handleFieldChange('isHidden', e.target.checked)}
                    />
                  }
                  label="Hide Bank Account"
                />

                {processor.createOnUserCreation && (
                  <>
                    {/* BVN SETTINGS */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={processor.useOwnerBVN}
                          onChange={(e) => handleFieldChange('useOwnerBVN', e.target.checked)}
                        />
                      }
                      label="Use Owner BVN"
                    />

                    {processor.useOwnerBVN && (
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, borderStyle: 'dashed' }}
                      >
                        <Stack spacing={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>BVN Mode</InputLabel>
                            <Select
                              value={processor.bvnMode || 'single'}
                              label="BVN Mode"
                              onChange={(e) => handleFieldChange('bvnMode', e.target.value)}
                            >
                              <MenuItem value="single">Single BVN</MenuItem>
                              <MenuItem value="multiple">Multiple BVNs</MenuItem>
                            </Select>
                          </FormControl>

                          {processor.bvnMode === 'multiple' ? (
                            <MultipleInput
                              label="Owner BVNs"
                              placeholder="Type BVN and press Enter or paste list"
                              values={processor.ownerBVNs || []}
                              usage={processor.bvnUsage || {}}
                              onChange={(newValues) => handleFieldChange('ownerBVNs', newValues)}
                            />
                          ) : (
                            <TextField
                              label="Owner BVN"
                              value={processor.ownerBVN || ''}
                              onChange={(e) => handleFieldChange('ownerBVN', e.target.value)}
                              fullWidth
                              helperText={`Accounts created: ${processor.bvnUsage?.[processor.ownerBVN] || 0} / ${
                                processor.maxAccountsPerBVN || 50
                              }`}
                            />
                          )}

                          <TextField
                            label="Max Accounts per BVN"
                            type="number"
                            value={processor.maxAccountsPerBVN !== undefined ? processor.maxAccountsPerBVN : 50}
                            onChange={(e) => handleFieldChange('maxAccountsPerBVN', parseInt(e.target.value, 10) || '')}
                            fullWidth
                            helperText="Number of accounts to create before moving to the next BVN"
                          />
                        </Stack>
                      </Paper>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    {/* NIN SETTINGS */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={processor.useOwnerNIN}
                          onChange={(e) => handleFieldChange('useOwnerNIN', e.target.checked)}
                        />
                      }
                      label="Use Owner NIN"
                    />

                    {processor.useOwnerNIN && (
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, borderStyle: 'dashed' }}
                      >
                        <Stack spacing={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>NIN Mode</InputLabel>
                            <Select
                              value={processor.ninMode || 'single'}
                              label="NIN Mode"
                              onChange={(e) => handleFieldChange('ninMode', e.target.value)}
                            >
                              <MenuItem value="single">Single NIN</MenuItem>
                              <MenuItem value="multiple">Multiple NINs</MenuItem>
                            </Select>
                          </FormControl>

                          {processor.ninMode === 'multiple' ? (
                            <MultipleInput
                              label="Owner NINs"
                              placeholder="Type NIN and press Enter or paste list"
                              values={processor.ownerNINs || []}
                              usage={processor.ninUsage || {}}
                              onChange={(newValues) => handleFieldChange('ownerNINs', newValues)}
                            />
                          ) : (
                            <TextField
                              label="Owner NIN"
                              value={processor.ownerNIN || ''}
                              onChange={(e) => handleFieldChange('ownerNIN', e.target.value)}
                              fullWidth
                              helperText={`Accounts created: ${processor.ninUsage?.[processor.ownerNIN] || 0} / ${
                                processor.maxAccountsPerNIN || 50
                              }`}
                            />
                          )}

                          <TextField
                            label="Max Accounts per NIN"
                            type="number"
                            value={processor.maxAccountsPerNIN !== undefined ? processor.maxAccountsPerNIN : 50}
                            onChange={(e) => handleFieldChange('maxAccountsPerNIN', parseInt(e.target.value, 10) || '')}
                            fullWidth
                            helperText="Number of accounts to create before moving to the next NIN"
                          />
                        </Stack>
                      </Paper>
                    )}
                  </>
                )}

                <Divider />

                {/* FEES */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={processor.isPercentageFee}
                      onChange={(e) => handleFieldChange('isPercentageFee', e.target.checked)}
                    />
                  }
                  label="Use Percentage Fee"
                />

                <TextField
                  label={processor.isPercentageFee ? 'Deposit Fee (%)' : 'Deposit Fee'}
                  value={processor.fee}
                  type="number"
                  onChange={(e) => handleFieldChange('fee', e.target.value)}
                  fullWidth
                />

                {processor.isPercentageFee && (
                  <TextField
                    label="Fee Cap"
                    value={processor.feeCap}
                    type="number"
                    onChange={(e) => handleFieldChange('feeCap', e.target.value)}
                    fullWidth
                    helperText="Maximum deduction amount"
                  />
                )}

                {/* BANKS */}
                {processor.availableBanks?.length > 0 && (
                  <FormControl fullWidth>
                    <InputLabel>Select banks</InputLabel>
                    <Select
                      multiple={processor.bankMultiSelect}
                      value={!Array.isArray(processor.banks) ? processor.banks?.split(',') || [] : processor.banks}
                      onChange={(e) => handleFieldChange('banks', e.target.value)}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {processor.availableBanks
                            ?.filter((b) => selected.includes(b.code))
                            .map((b) => (
                              <Chip key={b.code} label={b.name} />
                            ))}
                        </Box>
                      )}
                    >
                      {processor.availableBanks?.map((b) => (
                        <MenuItem key={b.code} value={b.code}>
                          {b.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <TextField
                  label="Webhook URL"
                  value={`${baseUrl}/api/v1/transaction/webhooks/${processor.name_id}`}
                  fullWidth
                  disabled
                />
              </Stack>
            </Box>

            {/* FOOTER */}
            <Box
              sx={{
                p: 2,
                borderTop: '1px solid',
                borderColor: 'grey.200',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              <Button onClick={() => setOpen(false)}>Cancel</Button>

              <LoadingButton
                loading={loading}
                variant="contained"
                onClick={() => {
                  onSave(processor);
                  setOpen(false);
                }}
              >
                Save Changes
              </LoadingButton>
            </Box>
          </Paper>
        </Box>
      </Modal>
    </>
  );
}

const getDefaultProcessors = (url) => [
  {
    name_id: 'monnify',
    name: 'Monnify',
    apiKey: '',
    secretKey: '',
    webhookUrl: `${url}/api/v1/transaction/webhooks/monnify`,
    contactCode: '',
    isActive: false,
    bankAccountIndex: 1,
    banks: '',
    availableBanks: [
      {
        name: 'Moniepoint',
        code: '50515',
      },
    ],
    bankMultiSelect: false,
    createOnUserCreation: false,
    useOwnerBVN: false,
    ownerBVN: '',
    useOwnerNIN: false,
    ownerNIN: '',
    bvnMode: 'single',
    ninMode: 'single',
    ownerBVNs: [],
    ownerNINs: [],
    maxAccountsPerBVN: 50,
    maxAccountsPerNIN: 50,
    fee: 50,
    isPercentageFee: false,
    feeCap: '',
    isHidden: false,
  },
  {
    name_id: 'billstack',
    name: 'Billstack',
    publicKey: '',
    secretKey: '',
    webhookUrl: `${url}/api/v1/transaction/webhooks/billstack`,
    isActive: false,
    bankAccountIndex: 2,
    banks: '',
    availableBanks: [
      {
        name: 'PALMPAY',
        code: 'PALMPAY',
      },
      {
        name: '9PSB',
        code: '9PSB',
      },
      {
        name: 'SAFEHAVEN',
        code: 'SAFEHAVEN',
      },
      {
        name: 'PROVIDUS',
        code: 'PROVIDUS',
      },
      {
        name: 'BANKLY',
        code: 'BANKLY',
      },
    ],
    bankMultiSelect: false,
    createOnUserCreation: false,
    useOwnerBVN: false,
    ownerBVN: '',
    useOwnerNIN: false,
    ownerNIN: '',
    bvnMode: 'single',
    ninMode: 'single',
    ownerBVNs: [],
    ownerNINs: [],
    maxAccountsPerBVN: 50,
    maxAccountsPerNIN: 50,
    fee: 50,
    isPercentageFee: false,
    feeCap: '',
    isHidden: false,
  },
  {
    name_id: 'aspfiy',
    name: 'Aspfiy',
    publicKey: '',
    secretKey: '',
    webhookUrl: `${url}/api/v1/transaction/webhooks/aspfiy`,
    isActive: false,
    bankAccountIndex: 3,
    banks: '',
    availableBanks: [
      {
        name: 'PAGA',
        code: 'PAGA',
      },
      {
        name: 'PALMPAY',
        code: 'PALMPAY',
      },
    ],
    bankMultiSelect: false,
    createOnUserCreation: false,
    useOwnerBVN: false,
    ownerBVN: '',
    useOwnerNIN: false,
    ownerNIN: '',
    bvnMode: 'single',
    ninMode: 'single',
    ownerBVNs: [],
    ownerNINs: [],
    maxAccountsPerBVN: 50,
    maxAccountsPerNIN: 50,
    fee: 50,
    isPercentageFee: false,
    feeCap: '',
    isHidden: false,
  },
  {
    name_id: 'paymentpoint',
    name: 'PaymentPoint',
    apiKey: '',
    secretKey: '',
    webhookUrl: `${url}/api/v1/transaction/webhooks/paymentpoint`,
    businessId: '',
    isActive: false,
    bankAccountIndex: 4,
    banks: '',
    availableBanks: [
      {
        name: 'PALMPAY',
        code: '20946',
      },
      {
        name: 'OPAY',
        code: '20897',
      },
    ],
    bankMultiSelect: false,
    createOnUserCreation: false,
    useOwnerBVN: false,
    ownerBVN: '',
    useOwnerNIN: false,
    ownerNIN: '',
    bvnMode: 'single',
    ninMode: 'single',
    ownerBVNs: [],
    ownerNINs: [],
    maxAccountsPerBVN: 50,
    maxAccountsPerNIN: 50,
    fee: 50,
    isPercentageFee: false,
    feeCap: '',
    isHidden: false,
  },
  {
    name_id: 'flutterwave',
    name: 'Flutterwave',
    publicKey: '',
    secretKey: '',
    webhookUrl: `${url}/api/v1/transaction/webhooks/flutterwave`,
    isActive: false,
    bankAccountIndex: 5,
    banks: '',
    availableBanks: [
      {
        name: 'WEMA',
        code: 'WEMA',
      },
    ],
    bankMultiSelect: false,
    createOnUserCreation: false,
    useOwnerBVN: false,
    ownerBVN: '',
    useOwnerNIN: false,
    ownerNIN: '',
    bvnMode: 'single',
    ninMode: 'single',
    ownerBVNs: [],
    ownerNINs: [],
    maxAccountsPerBVN: 50,
    maxAccountsPerNIN: 50,
    fee: 50,
    isPercentageFee: false,
    feeCap: '',
    isHidden: false,
  },
  {
    name_id: 'paystack',
    name: 'Paystack',
    publicKey: '',
    secretKey: '',
    webhookUrl: `${url}/api/v1/transaction/webhooks/paystack`,
    isActive: false,
    bankAccountIndex: 6,
    banks: '',
    availableBanks: [
      {
        name: 'WEMA',
        code: 'wema-bank',
      },
      {
        name: 'Access Bank',
        code: 'access-bank',
      },
    ],
    bankMultiSelect: false,
    createOnUserCreation: false,
    useOwnerBVN: false,
    ownerBVN: '',
    useOwnerNIN: false,
    ownerNIN: '',
    bvnMode: 'single',
    ninMode: 'single',
    ownerBVNs: [],
    ownerNINs: [],
    maxAccountsPerBVN: 50,
    maxAccountsPerNIN: 50,
    fee: 50,
    isPercentageFee: false,
    feeCap: '',
    isHidden: false,
  },
];

export default function PaymentSettings() {
  const navigate = useNavigate();
  const { activeApp } = useSelector((state) => state.auth);

  let selectedApp = activeApp;
  if (!selectedApp) {
    try {
      const bootstrapped = sessionStorage.getItem('bootstrappedApp');
      if (bootstrapped) {
        selectedApp = JSON.parse(bootstrapped);
      } else {
        const storedActive = localStorage.getItem('activeApp');
        if (storedActive) {
          selectedApp = JSON.parse(storedActive);
        }
      }
    } catch (e) {
      // ignore storage error
    }
  }

  const baseUrl = selectedApp?.urls?.apiUrl || '';

  const [currentView, setCurrentView] = useState('main'); // 'main', 'manual-funding-account', 'payment-processors'
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    })
  );

  // State for Manual Funding Account Section
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [bankName, setBankName] = useState('');
  const [instruction, setInstruction] = useState('');
  const [currentAccount, setCurrentAccount] = useState(null);
  const [editingManualAccount, setEditingManualAccount] = useState(false);
  const [isLoadingManualAccount, setIsLoadingManualAccount] = useState(false);
  const [loadingManualAccountSave, setLoadingManualAccountSave] = useState(false);
  const [validManualAccountForm, setValidManualAccountForm] = useState(false);

  // State for Payment Processors Section
  const [processors, setProcessors] = useState(() => getDefaultProcessors(baseUrl));
  const [isLoadingProcessors, setIsLoadingProcessors] = useState(false);
  const [loadingProcessorsSave, setLoadingProcessorsSave] = useState(false);
  const [paymentProcessorSetting, setPaymentProcessorSetting] = useState(false);
  const [showKYC, setShowKYC] = useState(false);

  const processorsRef = useRef(processors);
  useEffect(() => {
    processorsRef.current = processors;
  }, [processors]);

  // Manual Funding Account Handlers
  const handleGetManualFundingAccount = async () => {
    setIsLoadingManualAccount(true);
    const request = await getManualFundingAccount();
    if (request.ok) {
      setCurrentAccount(request.data.data);
    }
    setIsLoadingManualAccount(false);
  };

  const handleUpdateDetails = () => {
    if (currentAccount) {
      setAccountName(currentAccount.account_name);
      setAccountNumber(currentAccount.account_number);
      setBankName(currentAccount.bank_name);
      setInstruction(currentAccount.remark);
    }
    setEditingManualAccount(true);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = processors.findIndex((p) => p.name_id === active.id);
    const newIndex = processors.findIndex((p) => p.name_id === over.id);

    const reordered = arrayMove(processors, oldIndex, newIndex).map((item, index) => ({
      ...item,
      bankAccountIndex: index + 1,
    }));

    setProcessors(reordered);
    // update api

    const payload = {};

    reordered.forEach((item) => {
      payload[item.name_id] = item;
    });

    setLoadingProcessorsSave(true);
    const request = await updateAppSetting({
      payment_processor: payload,
      paymentProcessorSetting: paymentProcessorSetting ? 'env' : 'db',
    });
    if (request.ok) {
      toast.success('Payment processors updated successfully.');
    } else {
      toast.error('Failed to update payment processors.');
    }
  };

  const handleMoveProcessor = async (nameId, direction) => {
    // Sort processors by bankAccountIndex first to get the current visual order
    const sortedProcessors = [...processors].sort((a, b) => a.bankAccountIndex - b.bankAccountIndex);
    const index = sortedProcessors.findIndex((p) => p.name_id === nameId);
    if (index === -1) return;

    let targetIndex = index;
    if (direction === 'up' && index > 0) {
      targetIndex = index - 1;
    } else if (direction === 'down' && index < sortedProcessors.length - 1) {
      targetIndex = index + 1;
    }

    if (targetIndex === index) return; // No move possible

    const reordered = arrayMove(sortedProcessors, index, targetIndex).map((item, idx) => ({
      ...item,
      bankAccountIndex: idx + 1,
    }));

    setProcessors(reordered);

    const payload = {};
    reordered.forEach((item) => {
      payload[item.name_id] = item;
    });

    setLoadingProcessorsSave(true);
    const request = await updateAppSetting({
      payment_processor: payload,
      paymentProcessorSetting: paymentProcessorSetting ? 'env' : 'db',
    });
    if (request.ok) {
      toast.success('Payment processors order updated successfully.');
    } else {
      toast.error('Failed to update payment processors order.');
    }
    setLoadingProcessorsSave(false);
  };

  const handleUpdateManualFundingAccount = async () => {
    const payload = {
      bank_name: bankName,
      account_number: accountNumber,
      account_name: accountName,
      remark: instruction,
    };
    setLoadingManualAccountSave(true);
    const request = await updateManualFundingAccount(payload);
    if (request.ok) {
      setCurrentAccount(request.data.data);
      setEditingManualAccount(false);
      toast.success('Manual funding account updated successfully.');
    } else {
      toast.error('Failed to update manual funding account.');
    }
    setLoadingManualAccountSave(false);
  };

  useEffect(() => {
    if (instruction && accountName && accountNumber && bankName) {
      setValidManualAccountForm(true);
    } else {
      setValidManualAccountForm(false);
    }
  }, [bankName, accountName, accountNumber, instruction]);

  // Payment Processors Handlers
  const handleGetAppSetting = async () => {
    setIsLoadingProcessors(true);
    const request = await getAppSetting();
    if (request.ok) {
      const { data } = request.data;
      setPaymentProcessorSetting(data?.paymentProcessorSetting === 'env');
      setShowKYC(data?.createUserAsUnverified);
      if (data?.payment_processor) {
        const currentProcessors = Object.values(data?.payment_processor).map((item) => item);
        const defaults = getDefaultProcessors(baseUrl);
        setProcessors(
          defaults.map((item) => {
            const userProcessor = currentProcessors.find((data) => data.name_id === item.name_id);
            if (userProcessor) {
              return {
                ...item,
                ...userProcessor,
                availableBanks: item.availableBanks,
                bankMultiSelect: item.bankMultiSelect,
                isPercentageFee: userProcessor.isPercentageFee !== undefined ? userProcessor.isPercentageFee : false,
                isHidden: userProcessor.isHidden !== undefined ? userProcessor.isHidden : false,
                feeCap: userProcessor.feeCap !== undefined ? userProcessor.feeCap : '',
                webhookUrl: item.webhookUrl || `${baseUrl}/api/v1/transaction/webhooks/${item.name_id}`,
                bankAccountIndex:
                  userProcessor.bankAccountIndex !== undefined ? userProcessor.bankAccountIndex : item.bankAccountIndex,
              };
            }
            return item;
          })
        );
      }
    }
    setIsLoadingProcessors(false);
  };

  const handleUpdateAppSetting = async (updatedProcessor = null) => {
    setLoadingProcessorsSave(true);
    const payload = {};

    processorsRef.current.forEach((item) => {
      if (updatedProcessor && item.name_id === updatedProcessor.name_id) {
        payload[item.name_id] = updatedProcessor;
      } else {
        payload[item.name_id] = item;
      }
    });

    const request = await updateAppSetting({
      payment_processor: payload,
      paymentProcessorSetting: 'db',
      createUserAsUnverified: showKYC,
    });
    if (request.ok) {
      await handleGetAppSetting();
      toast.success('Payment processor updated successfully');
      setLoadingProcessorsSave(false);
      return;
    }
    setLoadingProcessorsSave(false);
    toast.error(request.data?.message || 'Unable to update payment processors');
  };

  const handleChangeShowKyc = async (checked) => {
    setShowKYC(checked);
    const request = await updateAppSetting({
      createUserAsUnverified: checked,
    });
    if (request.ok) {
      toast.success('User KYC configuration updated successfully');
      return;
    }

    toast.error('Failed to update User KYC configuration');
    setShowKYC(!checked);
  };

  // Ensure only one processor is marked as default at any time.
  const handleProcessorChange = (updatedProcessor) => {
    setProcessors((prev) =>
      prev.map((proc) => {
        if (proc.name_id === updatedProcessor.name_id) {
          return updatedProcessor;
        }
        if (updatedProcessor.isDefault) {
          return { ...proc, isDefault: false };
        }
        return proc;
      })
    );
  };

  // Initial data fetching based on current view
  useEffect(() => {
    if (currentView === 'manual-funding-account') {
      handleGetManualFundingAccount();
    } else if (currentView === 'payment-processors') {
      handleGetAppSetting();
    }
  }, [currentView]); // Re-fetch when view changes

  const renderContent = () => {
    switch (currentView) {
      case 'main':
        return (
          <Grid container spacing={3}>
            {paymentCategories.map((category) => (
              <Grid item xs={12} sm={6} md={4} key={category.id}>
                <Paper
                  component={ButtonBase}
                  onClick={() => setCurrentView(category.id)}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    p: 3,
                    borderRadius: 3,
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    textAlign: 'left',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'grey.100',
                      borderColor: 'primary.main',
                      boxShadow: '0 6px 12px -2px rgb(0 0 0 / 0.15), 0 3px 6px -2px rgb(0 0 0 / 0.08)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box sx={{ color: 'primary.main', mb: 2 }}>{category.icon}</Box>
                  <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
                    {category.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {category.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        );
      case 'manual-funding-account':
        return (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)',
              border: '1px solid',
              borderColor: 'grey.200',
              bgcolor: 'white',
              overflow: 'hidden',
              p: { xs: 2, md: 3 },
            }}
          >
            <Stack spacing={3}>
              <Box>
                <Typography variant="h5" fontWeight={700} color="text.primary">
                  Manual Funding Account
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                  Manage bank details for manual deposits.
                </Typography>
              </Box>
              {editingManualAccount ? (
                <Stack spacing={2}>
                  <TextField
                    label="Account Number"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    label="Account Name"
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    label="Bank Name"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    label="Customer Instruction"
                    multiline
                    rows={4}
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      onClick={() => setEditingManualAccount(false)}
                      disabled={isLoadingManualAccount}
                      sx={{ borderRadius: 2, px: 3, py: 1.5 }}
                    >
                      Cancel
                    </Button>
                    <LoadingButton
                      loading={loadingManualAccountSave}
                      variant="contained"
                      disabled={!validManualAccountForm}
                      onClick={handleUpdateManualFundingAccount}
                      sx={{ borderRadius: 2, px: 3, py: 1.5 }}
                    >
                      Submit
                    </LoadingButton>
                  </Stack>
                </Stack>
              ) : isLoadingManualAccount ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                  <CircularProgress />
                </Box>
              ) : currentAccount ? (
                <Stack spacing={2} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body1" fontWeight={500}>
                      Account Name
                    </Typography>
                    <Typography variant="body1">{currentAccount.account_name}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body1" fontWeight={500}>
                      Account Number
                    </Typography>
                    <Typography variant="body1">{currentAccount.account_number}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body1" fontWeight={500}>
                      Bank
                    </Typography>
                    <Typography variant="body1">{currentAccount.bank_name}</Typography>
                  </Stack>
                  <Box>
                    <Typography variant="body1" fontWeight={500} sx={{ mb: 1 }}>
                      Instruction
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentAccount.remark}
                    </Typography>
                  </Box>
                  <Button variant="outlined" onClick={handleUpdateDetails} sx={{ borderRadius: 2, px: 3, py: 1.5 }}>
                    Update Details
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={2} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                  <Typography variant="body1" color="text.secondary">
                    No manual funding account setup found.
                  </Typography>
                  <LoadingButton
                    loading={isLoadingManualAccount}
                    variant="outlined"
                    onClick={() => setEditingManualAccount(true)}
                    sx={{ borderRadius: 2, px: 3, py: 1.5 }}
                  >
                    Setup Now
                  </LoadingButton>
                </Stack>
              )}
            </Stack>
          </Paper>
        );
      case 'payment-processors':
        return (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'grey.200',
              bgcolor: 'background.paper',
              p: { xs: 2, md: 3 },
            }}
          >
            <Stack spacing={3}>
              {/* HEADER */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    Payment Processors
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Configure API keys and gateway behavior
                  </Typography>
                </Box>

                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography fontWeight={500}>Show User As KYC Verified</Typography>

                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.3,
                      borderRadius: 2,
                      fontSize: 12,
                      fontWeight: 600,
                      bgcolor: showKYC ? 'success.light' : 'error.light',
                      color: showKYC ? 'success.dark' : 'error.dark',
                    }}
                  >
                    {showKYC ? 'ENABLED' : 'DISABLED'}
                  </Box>

                  <Switch checked={showKYC} onChange={(e) => handleChangeShowKyc(e.target.checked)} />
                </Stack>
              </Stack>

              {/* CONTENT */}
              {isLoadingProcessors ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 200,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={processors.map((i) => i.name_id)} strategy={verticalListSortingStrategy}>
                    <Stack direction="column" spacing={2.5}>
                      {processors
                        .sort((a, b) => a.bankAccountIndex - b.bankAccountIndex)
                        .map((processor, idx) => (
                          <Box key={processor.name_id} sx={{ width: '100%' }}>
                            <PaymentProcessorCard
                              processor={processor}
                              baseUrl={baseUrl}
                              onChange={handleProcessorChange}
                              onSave={handleUpdateAppSetting}
                              loading={loadingProcessorsSave}
                              onMove={handleMoveProcessor}
                              isFirst={idx === 0}
                              isLast={idx === processors.length - 1}
                            />
                          </Box>
                        ))}
                    </Stack>
                  </SortableContext>
                </DndContext>
              )}
            </Stack>
          </Paper>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: { xs: 2, md: 4 } }}>
          {currentView === 'main' ? (
            <IconButton
              onClick={() => navigate('/dashboard/settings')} // Back to main dashboard settings
              sx={{
                bgcolor: 'grey.100',
                '&:hover': { bgcolor: 'grey.200' },
              }}
            >
              <ArrowLeft size={20} />
            </IconButton>
          ) : (
            <IconButton
              onClick={() => setCurrentView('main')} // Back to Payment Settings main view
              sx={{
                bgcolor: 'grey.100',
                '&:hover': { bgcolor: 'grey.200' },
              }}
            >
              <ArrowLeft size={20} />
            </IconButton>
          )}
          <Typography variant="h5" fontWeight={700} color="text.primary">
            {currentView === 'main'
              ? 'Payment Settings'
              : paymentCategories.find((cat) => cat.id === currentView)?.title}
          </Typography>
        </Stack>

        {renderContent()}
      </Box>
    </Box>
  );
}
